import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Tag } from "lucide-react";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import LazyImage from "@/components/LazyImage";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["All", "Planning", "Workspace Design", "Comparison", "Cost Management", "Enterprise"];

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useCanonical("https://ezeefitofficesystems.in/blog");
  useMetaDescription("Ezeefit knowledge blog — expert insights on modular office furniture, workspace design, office planning & corporate setup solutions in Mumbai.");

  useEffect(() => {
    document.title = "Office Furniture Blog | Workspace Design Tips & Corporate Furniture Insights | Ezeefit Mumbai";
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blogs`);
        setBlogs(response.data);
        setFilteredBlogs(response.data);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (selectedCategory === "All") {
      setFilteredBlogs(blogs);
    } else {
      setFilteredBlogs(blogs.filter((blog) => blog.category === selectedCategory));
    }
  }, [selectedCategory, blogs]);

  return (
    <div className="pt-20" data-testid="blog-page">
      <Helmet>
        <meta name="description" content="Ezeefit knowledge blog — expert insights on modular office furniture, workspace design, office planning & corporate setup solutions in Mumbai." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/blog" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"}, {"@type": "ListItem", "position": 2, "name": "Blog", "item": "https://ezeefitofficesystems.in/blog"}]}) }} />
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Knowledge Hub
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Insights & Resources
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
              Expert guidance on office furniture planning, workspace design, and operational efficiency.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <Tag className="w-5 h-5 text-[#64748B] shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? "bg-[#0F172A] text-white"
                    : "bg-[#F1F5F9] text-[#334155] hover:bg-[#E2E8F0]"
                }`}
                data-testid={`blog-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full mx-auto" />
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#64748B]">No articles found for this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <motion.article
                  key={blog.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white border border-[#E2E8F0] blog-card group"
                >
                  <Link to={`/blog/${blog.slug}`} className="block" data-testid={`blog-card-${blog.slug}`}>
                    <div className="relative overflow-hidden card-image-zoom">
                      <div className="aspect-[16/10]">
                        <LazyImage
                          src={blog.image}
                          alt={blog.title}
                          containerClassName="w-full h-full"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#F97316] text-white text-xs font-mono px-3 py-1 uppercase tracking-wider">
                          {blog.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-[#64748B] mb-3">
                        <span>{blog.published_at}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {blog.read_time}
                        </span>
                      </div>

                      <h2 className="text-xl font-bold text-[#0F172A] mb-3 group-hover:text-[#F97316] transition-colors line-clamp-2">
                        {blog.title}
                      </h2>
                      
                      <p className="text-[#64748B] text-sm line-clamp-3 mb-4">
                        {blog.excerpt}
                      </p>

                      <div className="flex items-center gap-2 text-[#F97316] font-semibold text-sm">
                        Read More
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <ExploreMore currentPage="blog" />
    </div>
  );
}
